import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { IconArrowLeft, IconGavel } from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

type ResolutionAction =
	| "release_to_runner"
	| "refund_to_customer"
	| "partial_release";

const DISPUTE_TYPE_LABELS: Record<string, string> = {
	delivery_timeout: "Delivery Timeout",
	dispute_raised: "Dispute Raised",
	large_amount_review: "Large Amount Review",
};

const RESOLUTION_LABELS: Record<ResolutionAction, string> = {
	release_to_runner: "Release funds to runner",
	refund_to_customer: "Refund to customer",
	partial_release: "Partial release (manual split)",
};

export const Route = createFileRoute("/_authenticated/disputes/$disputeId")({
	component: DisputeDetailPage,
});

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function DisputeDetailPage() {
	const { disputeId } = Route.useParams();
	const data = useQuery(api.admin.getDisputeDetails, {
		disputeId: disputeId as never,
	});
	const resolveDispute = useMutation(api.disputes.resolve);

	const [resolutionAction, setResolutionAction] =
		useState<ResolutionAction>("release_to_runner");
	const [resolutionNotes, setResolutionNotes] = useState("");
	const [isResolving, setIsResolving] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	if (data === undefined) {
		return (
			<div className="container mx-auto space-y-6 px-4 py-10 lg:px-6">
				<Skeleton className="h-8 w-48" />
				<div className="grid gap-6 lg:grid-cols-2">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	if (data === null) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<p className="text-muted-foreground">Dispute not found.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/disputes">Back to Disputes</Link>
				</Button>
			</div>
		);
	}

	const {
		dispute,
		errand,
		customer,
		runner,
		escrow,
		negotiation,
		resolver,
		raiser,
	} = data;
	const isResolved = Boolean(dispute.resolvedAt);

	const handleResolve = async () => {
		if (!resolutionNotes.trim()) {
			setMessage("Please provide resolution notes.");
			return;
		}
		setIsResolving(true);
		setMessage(null);
		try {
			await resolveDispute({
				disputeId: disputeId as never,
				resolutionAction,
				resolutionNotes: resolutionNotes.trim(),
			});
			setMessage("Dispute resolved successfully.");
		} catch {
			setMessage("Failed to resolve dispute.");
		} finally {
			setIsResolving(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-6 flex items-center gap-4">
				<Button asChild size="sm" variant="ghost">
					<Link to="/disputes">
						<IconArrowLeft className="mr-1 h-4 w-4" />
						Back
					</Link>
				</Button>
				<h1 className="font-bold text-2xl">Dispute Detail</h1>
				<Badge variant="secondary">
					{DISPUTE_TYPE_LABELS[dispute.disputeType] ?? dispute.disputeType}
				</Badge>
				{isResolved ? (
					<Badge className="bg-green-500">Resolved</Badge>
				) : (
					<Badge variant="destructive">Open</Badge>
				)}
			</div>

			<div className="grid gap-6 lg:grid-cols-2">
				{/* Dispute Info */}
				<Card>
					<CardHeader>
						<CardTitle>Dispute Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="text-sm">
							<span className="text-muted-foreground">Raised by:</span>{" "}
							{raiser ? raiser.name : "System (automatic)"}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Reason:</span>{" "}
							{dispute.reason ?? "No reason provided"}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Created:</span>{" "}
							{new Date(dispute.createdAt).toLocaleString()}
						</div>
						{isResolved && (
							<>
								<div className="text-sm">
									<span className="text-muted-foreground">Resolved by:</span>{" "}
									{resolver?.name ?? "Unknown"}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Resolved at:</span>{" "}
									{dispute.resolvedAt
										? new Date(dispute.resolvedAt).toLocaleString()
										: "N/A"}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Action:</span>{" "}
									{dispute.resolutionAction
										? (RESOLUTION_LABELS[
												dispute.resolutionAction as ResolutionAction
											] ?? dispute.resolutionAction)
										: "N/A"}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Notes:</span>{" "}
									{dispute.resolutionNotes ?? "None"}
								</div>
							</>
						)}
					</CardContent>
				</Card>

				{/* Errand Context */}
				<Card>
					<CardHeader>
						<CardTitle>Errand Context</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="text-sm">
							<span className="text-muted-foreground">Title:</span>{" "}
							<Link
								to="/errands/$errandId"
								params={{ errandId: errand._id }}
								className="font-medium text-blue-600 underline-offset-2 hover:underline"
							>
								{errand.title}
							</Link>
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">State:</span>{" "}
							<Badge variant="secondary">{errand.state}</Badge>
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">Description:</span>{" "}
							{errand.fullDescription}
						</div>
						{errand.pickupAddress && (
							<div className="text-sm">
								<span className="text-muted-foreground">Pickup:</span>{" "}
								{errand.pickupAddress}
							</div>
						)}
						{errand.destinationAddress && (
							<div className="text-sm">
								<span className="text-muted-foreground">Destination:</span>{" "}
								{errand.destinationAddress}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Parties */}
				<Card>
					<CardHeader>
						<CardTitle>Parties</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="rounded-md border p-3">
							<p className="mb-1 font-medium text-sm">Customer</p>
							{customer ? (
								<>
									<p className="text-sm">
										<Link
											to="/customers/$userId"
											params={{ userId: customer._id }}
											className="text-blue-600 underline-offset-2 hover:underline"
										>
											{customer.name}
										</Link>
									</p>
									<p className="text-muted-foreground text-xs">
										{customer.email}
									</p>
									<p className="text-muted-foreground text-xs">
										{customer.phoneNumber ?? "No phone"}
									</p>
								</>
							) : (
								<p className="text-muted-foreground text-sm">Unknown</p>
							)}
						</div>
						<div className="rounded-md border p-3">
							<p className="mb-1 font-medium text-sm">Runner</p>
							{runner ? (
								<>
									<p className="text-sm">
										<Link
											to="/runners/$userId"
											params={{ userId: runner._id }}
											className="text-blue-600 underline-offset-2 hover:underline"
										>
											{runner.name}
										</Link>
									</p>
									<p className="text-muted-foreground text-xs">
										{runner.email}
									</p>
									<p className="text-muted-foreground text-xs">
										{runner.phoneNumber ?? "No phone"}
									</p>
								</>
							) : (
								<p className="text-muted-foreground text-sm">
									No runner assigned
								</p>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Financial Context */}
				<Card>
					<CardHeader>
						<CardTitle>Financial Context</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{escrow ? (
							<>
								<div className="text-sm">
									<span className="text-muted-foreground">Service Fee:</span>{" "}
									{formatNaira(escrow.serviceFee)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Goods Cost:</span>{" "}
									{formatNaira(escrow.goodsCost)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Total Escrowed:</span>{" "}
									{formatNaira(escrow.totalAmount)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Escrow Status:</span>{" "}
									<Badge variant="secondary">{escrow.status}</Badge>
								</div>
							</>
						) : (
							<p className="text-muted-foreground text-sm">No escrow found.</p>
						)}
						{negotiation && (
							<div className="mt-2 rounded-md border p-3">
								<p className="mb-1 font-medium text-sm">Last Negotiation</p>
								<div className="text-sm">
									Goods: {formatNaira(negotiation.goodsCost)} | Service:{" "}
									{formatNaira(negotiation.serviceFee)}
								</div>
								<div className="text-muted-foreground text-xs">
									Status: {negotiation.status}
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Resolution Panel */}
			{!isResolved && (
				<Card className="mt-6">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconGavel className="h-5 w-5" />
							Resolve Dispute
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<p className="font-medium text-sm">Resolution Action</p>
							<Select
								onValueChange={(value) =>
									setResolutionAction(value as ResolutionAction)
								}
								value={resolutionAction}
							>
								<SelectTrigger className="w-[300px]">
									<SelectValue placeholder="Select action" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="release_to_runner">
										Release funds to runner
									</SelectItem>
									<SelectItem value="refund_to_customer">
										Refund to customer
									</SelectItem>
									<SelectItem value="partial_release">
										Partial release (manual split)
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<p className="font-medium text-sm">Resolution Notes</p>
							<Textarea
								onChange={(e) => setResolutionNotes(e.target.value)}
								placeholder="Explain the resolution decision..."
								rows={4}
								value={resolutionNotes}
							/>
						</div>
						<Button disabled={isResolving} onClick={handleResolve}>
							{isResolving ? "Resolving..." : "Submit Resolution"}
						</Button>
						{message && (
							<p className="text-muted-foreground text-sm">{message}</p>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
